import { useCallback, useEffect, useMemo, useState } from "react";
import { adminFetch } from "utils/adminFetch";
import { formatExpiringDate } from "utils/formatExpiringDate";

function formatTypeLabel(accountType) {
  const t = String(accountType || "").trim();
  if (!t) return "—";
  return t.toLowerCase();
}

function formatSubscriptionDate(iso) {
  return formatExpiringDate(iso);
}

export default function AdminSubscribers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/subscribers");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not load subscribers.");
        setRows([]);
        return;
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch {
      setError("Network error.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const c = Number(a.clientId) - Number(b.clientId);
        if (c !== 0) return c;
        return Number(a.slot) - Number(b.slot);
      }),
    [rows],
  );

  const clientGroups = useMemo(() => {
    const groups = [];
    for (const row of sortedRows) {
      const last = groups[groups.length - 1];
      if (!last || last.clientId !== row.clientId) {
        groups.push({ clientId: row.clientId, rows: [row] });
      } else {
        last.rows.push(row);
      }
    }
    return groups;
  }, [sortedRows]);

  const uniqueClients = clientGroups.length;
  const totalSlots = sortedRows.filter((row) => row.tradingAccountId).length;

  return (
    <div className="mt-3 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            Subscribers
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Registered clients from ID 426051 (426051S1, 426052S1, …) with account,
            server, subscription and expiry dates.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadRows()}
          disabled={loading}
          className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-60"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-[20px] border border-gray-200 bg-white px-5 py-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
            Clients
          </p>
          <p className="mt-1 text-3xl font-bold text-navy-700 dark:text-white">
            {loading ? "…" : uniqueClients}
          </p>
        </div>
        <div className="rounded-[20px] border border-gray-200 bg-white px-5 py-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
            Slots
          </p>
          <p className="mt-1 text-3xl font-bold text-navy-700 dark:text-white">
            {loading ? "…" : totalSlots}
          </p>
        </div>
      </div>

      {error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-[20px] border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        <table className="w-full min-w-[1200px] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs font-bold uppercase tracking-wide text-gray-600 dark:border-white/10 dark:text-gray-400">
              <th className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                Client ID
              </th>
              <th className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                Site password
              </th>
              <th className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                Slots
              </th>
              <th className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                Type
              </th>
              <th className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                Account
              </th>
              <th className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                Server
              </th>
              <th className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                MT password
              </th>
              <th className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                Subscription date
              </th>
              <th className="px-4 py-3">Expiring date</th>
            </tr>
          </thead>
          <tbody>
            {loading && sortedRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                  No registered clients yet (from ID 426051).
                </td>
              </tr>
            ) : (
              clientGroups.map((group) =>
                group.rows.map((row, rowIndex) => {
                  const subscription =
                    row.subscriptionDate || row.activatingDate || null;
                  const rowKey = `${row.clientId}-S${row.slot}`;

                  return (
                    <tr
                      key={rowKey}
                      className="border-b border-gray-200 align-middle dark:border-white/10"
                    >
                      {rowIndex === 0 ? (
                        <td
                          rowSpan={group.rows.length}
                          className="border-r border-gray-200 bg-gray-50/80 px-4 py-3 text-center font-bold tabular-nums text-navy-800 dark:border-white/10 dark:bg-navy-900/50 dark:text-white"
                        >
                          <div>{group.clientId}</div>
                          {group.rows[0]?.clientName || group.rows[0]?.email ? (
                            <div className="mt-1 text-[11px] font-normal normal-case text-gray-500 dark:text-gray-400">
                              {group.rows[0]?.clientName || group.rows[0]?.email}
                            </div>
                          ) : null}
                          {group.rows[0]?.username ? (
                            <div className="mt-0.5 text-[10px] font-normal normal-case text-gray-500 dark:text-gray-400">
                              @{group.rows[0].username}
                            </div>
                          ) : null}
                        </td>
                      ) : null}
                      {rowIndex === 0 ? (
                        <td
                          rowSpan={group.rows.length}
                          className="border-r border-gray-200 px-4 py-3 align-middle dark:border-white/10"
                        >
                          {group.rows[0]?.siteLoginPassword ? (
                            <span className="font-mono text-sm font-semibold text-navy-800 dark:text-gray-100">
                              {group.rows[0].siteLoginPassword}
                            </span>
                          ) : (
                            <span
                              className="text-xs italic text-amber-700 dark:text-amber-300"
                              title="Compte créé avant l'enregistrement admin — le client doit réinitialiser son mot de passe site une fois pour qu'il apparaisse ici."
                            >
                              Non enregistré
                            </span>
                          )}
                        </td>
                      ) : null}
                      <td className="border-r border-gray-200 px-4 py-3 font-mono font-semibold text-navy-700 dark:border-white/10 dark:text-white">
                        {row.tradingAccountId || "—"}
                      </td>
                      <td className="border-r border-gray-200 px-4 py-3 font-semibold lowercase dark:border-white/10">
                        {formatTypeLabel(row.accountType)}
                      </td>
                      <td className="border-r border-gray-200 px-4 py-3 tabular-nums dark:border-white/10">
                        {row.accountNumber || "—"}
                      </td>
                      <td className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                        {row.server || "—"}
                      </td>
                      <td className="border-r border-gray-200 px-4 py-3 align-middle dark:border-white/10">
                        {row.tradingPassword ? (
                          <span className="font-mono text-sm font-semibold text-navy-800 dark:text-gray-100">
                            {row.tradingPassword}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="border-r border-gray-200 px-4 py-3 dark:border-white/10">
                        {formatSubscriptionDate(subscription)}
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        {formatExpiringDate(row.expiringDate)}
                      </td>
                    </tr>
                  );
                }),
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
